/**
 * Types for the Meskernel merchandiser-sensor BLE configuration protocol.
 *
 * These units (the Motor City ice-cooler telemetry devices) expose a GATT table
 * where reads carry status/settings and a single writable "command" channel
 * (`fff00200`) accepts configuration commands. This module is the pure,
 * platform-agnostic codec: it parses the readable characteristics and builds the
 * command bytes. The BLE transport lives in web-ui; the UI lives in the
 * distribution module. See `docs/claude/sensor-ble-config-handoff.md`.
 */

/**
 * Command codes accepted by the `fff00200` command channel. Each is a single
 * ASCII character; the wire form is `:<deviceId>:<code>:<value>\r\n`. Confirmed
 * by reverse-engineering the vendor tool and by writing to a live unit
 * (2026-08-25).
 */
export enum SensorCommandCode {
	/** Reboot the device. */
	Reset = '0',
	/** `upp` — report interval (device-defined unit). */
	ReportInterval = '1',
	/** `LD_n` — number of sensor modules the unit expects. */
	ModuleCount = '2',
	/** Tenant API key. */
	ApiKey = '5',
	/** Server URL. */
	ServerUrl = '7',
	/** SIM APN name. */
	ApnName = '8',
	/** Start module-addressing mode (re-commission only — wipes addressing). */
	EnableAddressing = '9',
	/** Clear all module addresses (re-commission only — wipes addressing). */
	ClearAddressing = 'b',
}

/** One entry of the `onine_dev` per-module address map. `FF` = unaddressed. */
export interface SensorModuleAddressModel {
	/** Module slot index (the `[n]` in `onine_dev`). */
	index: number;
	/** Assigned address, or `FF` when the slot is unaddressed. */
	address: string;
}

/**
 * Parsed contents of the device settings block (`fff00105`). Numeric fields are
 * `null` when the device omitted them or they weren't parseable, so callers must
 * treat them as optional rather than assuming presence.
 */
export interface SensorSettingsModel {
	/** `url` — CRM server the unit reports to. */
	serverUrl: string | null;
	/** `api_key` — tenant key (note: exposed as a plaintext BLE read). */
	apiKey: string | null;
	/** `apn_name` — SIM APN. */
	apnName: string | null;
	/** `LD_n` — module count. */
	moduleCount: number | null;
	/**
	 * `upp` — report interval in SECONDS. Confirmed on hardware 2026-08-26:
	 * unit MPY26CK4U0036490P was written `upp=30` and submits to the CRM
	 * every 30 seconds (two ingest rows per minute, sustained).
	 */
	reportInterval: number | null;
	/** `addr_encode_mode`. */
	addrEncodeMode: number | null;
	/** `onine_dev` — per-module address map. */
	moduleAddresses: Array<SensorModuleAddressModel>;
	/** The original block, preserved for debugging / round-trip. */
	raw: string;
}

/** Parsed device identity (`fff00100`): `mfn,dn,sn,hwv,fwv`. */
export interface SensorIdentityModel {
	/** Manufacturer name. */
	manufacturer: string | null;
	/** Device name / type. */
	deviceName: string | null;
	/** Serial number. */
	serialNumber: string | null;
	/** Hardware version. */
	hardwareVersion: string | null;
	/** Firmware version. */
	firmwareVersion: string | null;
	raw: string;
}

/**
 * A parsed command result echoed back by the `fff00200` read after a command:
 * `:<deviceId>:<code>:<result>` (e.g. `:A7D3E7EC:1:success`).
 */
export interface SensorCommandResultModel {
	deviceId: string;
	code: string;
	/** Status word, e.g. `success`. */
	result: string;
	raw: string;
}

/**
 * Classification of what a `fff00200` read shows after we wrote a command.
 *
 * The device buffers whatever bytes we write; a read returns EITHER a
 * device-generated result (`:id:code:<word>`) meaning the command was parsed, OR
 * our own bytes read straight back (with a stale tail) meaning it was ignored.
 * This is the fastest signal for "did the unit accept the command?".
 */
export interface SensorEchoClassification {
	/** The device parsed the command and produced a result. */
	parsed: boolean;
	/** The status word from the device result, when `parsed`. */
	result: string | null;
	/** The read was just our own written bytes, not a device result. */
	rawReadback: boolean;
	/**
	 * The cleaned echo text as read from the channel — surfaced so an
	 * unacknowledged command can show WHAT the unit answered (on-site
	 * debugging need: a bare "did not acknowledge" hides whether the
	 * buffer held our bytes, a device result, or something new).
	 */
	rawEcho: string;
}

/**
 * Parsed cell/battery status block (`fff00101`). Every numeric field is
 * `null` when the device omitted it or it did not parse, so callers must
 * treat them as optional.
 */
export interface SensorStatusModel {
	/** `power` — power source (1 = AC, 0 = battery on the ingest side). */
	powerSource: number | null;
	/** `chg` — charging flag. */
	charging: number | null;
	/** `vbat` — battery voltage in mV. */
	batteryMillivolts: number | null;
	/** `tmpr` — 4G module temperature in °C. */
	temperatureC: number | null;
	/** `net` — network type / radio access technology. */
	networkType: string | null;
	/** `reg` — cellular registration: 1 = registered, 0 = not registered. */
	registered: number | null;
	/** `csq` — signal quality (0–31 on the ingest side). */
	signalQuality: number | null;
	/** `ss` — signal strength in dBm. */
	signalStrength: number | null;
	/** `ber` — bit error rate. */
	bitErrorRate: number | null;
	/** `rsrq` — reference signal received quality. */
	referenceSignalQuality: number | null;
	/** `rsrp` — reference signal received power. */
	referenceSignalPower: number | null;
	/** The original block, preserved for debugging. */
	raw: string;
}
